import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { LoadingSpinner, notify } from 'legacy-packages/ft-cb';
import axios from 'axios';
import dateTimeFormat from 'legacy-src/utility/DateTime';
import { sendToTelemetry, TLTRY_DEFAULTS } from 'legacy-src/utility/telemetry';
import cn from 'classnames';
import styles from './index.module.scss';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

const CONSTANTS = {
  LABEL: 'Export details',
  TARGET: 'export-details-button',
  TOOLTIP: 'Export the displayed assignments to a CSV file',
  ARIA_LABEL: {
    'aria-label': 'export details',
  },
  TITLE: 'export details',
};

const RESOURCE_TYPES = [
  {
    name: 'All',
    value: 'all',
  },
  {
    name: 'Progress Checks',
    value: 'ppc',
  },
  {
    name: 'My Quizzes',
    value: 'qb',
  },
  {
    name: 'Practice Exams',
    value: 'pe',
  },
  {
    name: 'Videos',
    value: 'video',
  },
];

const getStudentId = (activeStudent) => {
  if (!Number.isInteger(activeStudent.value)) {
    const id = activeStudent.value.split('-')[1];
    return {
      id: id > 0 ? id : null,
      label: `Student ${activeStudent.firstName} ${activeStudent.lastName}`,
    };
  }
  return null;
};

const downloadFile = (
  data,
  subjectName,
  resourceType,
  activeSection,
  activeStudent,
) => {
  const timestamp = dateTimeFormat(new Date(), 'dateTimeFile');

  // Filename pattern default: <Subject> All Assignments (<Resource Type> if not all) Report for <Class Name> <YYYYMMDD-time_string>.csv
  // Filename pattern filtering by Student: <Subject> All Assignments (<Resource Type> if not all) Report for Student firstName lasName (<Class Name>) <YYYYMMDD-time_string>.csv
  const filename = `${subjectName} All Assignments ${RESOURCE_TYPES.filter(
    (t) => t.value === resourceType,
  ).map((t) => `(${t.name})`)} Report for ${
    activeStudent ? `${activeStudent.label} (${activeSection})` : activeSection
  } ${timestamp}`;

  let a = document.createElement('a');

  a.type = 'text/csv';
  a.download = `${filename}.csv`;
  a.href = window.URL.createObjectURL(data);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const ExportDetails = ({
  enableExportDetails,
  router,
  teacherSubjects,
  resourceType,
  filterProps,
  classNames,
  featureFlagExportAllAssignments,
}) => {
  const [isLoading, setLoading] = useState(false);
  const { activeSection, activeEducationPeriod, activeStudent } = filterProps;
  const {
    params: { subject: masterSubjectId },
  } = router;
  const student = getStudentId(activeStudent);
  const subjectName = teacherSubjects
    .filter((item) => item.id === masterSubjectId)
    .map((item) => item.name)[0];

  const fetchCSVExportedData = async () => {
    setLoading(true);
    const params = {
      master_subject_id: masterSubjectId,
      education_period: activeEducationPeriod.value,
      section_id: activeSection.id,
      resource_type: resourceType !== 'all' ? resourceType : null,
      student_id: student ? student.id : null,
    };

    const res = await axios
      .get(`${FYM_BACKEND_REPORTING_URL}/api/all_assignment_report_export`, {
        params,
        responseType: 'blob',
      })
      .catch(function (error) {
        notify.error(`${error}`, { timeout: 5 });
      });

    // When the backend endpoint returns an empty set of data the status
    // is 204, in this case no error will be displayed and
    if (![200, 204].includes(res.status)) {
      notify.error('Error!', { timeout: 5 });
      return;
    }

    downloadFile(
      res.data,
      subjectName,
      params.resource_type,
      activeSection.label,
      student,
    );
    setLoading(false);

    sendToTelemetry(
      Object.assign(
        {
          app: 'fym',
          params,
          event_type: 'all_assignments_export',
        },
        TLTRY_DEFAULTS[0],
      ),
    );
  };

  return (
    <div
      className={cn(
        featureFlagExportAllAssignments ? '' : 'hidden',
        classNames,
      )}
    >
      {isLoading && (
        <div className={styles['activity-results-dashboard__loading-popover']}>
          <span
            className={styles['activity-results-dashboard__loading-spinner']}
          >
            <LoadingSpinner />
            <div className="text">This may take up to 30 seconds.</div>
          </span>
        </div>
      )}
      <ButtonWithIcon
        isNaked
        icon="export_data"
        className="mt-5"
        onClick={() => {
          fetchCSVExportedData();
        }}
        accessibility={CONSTANTS.ARIA_LABEL}
        disabled={enableExportDetails}
      >
        Export Details
      </ButtonWithIcon>
    </div>
  );
};

ExportDetails.propTypes = {
  enableExportDetails: PropTypes.bool.isRequired,
  filterProps: PropTypes.object.isRequired,
  resourceType: PropTypes.string,
  classNames: PropTypes.string,
};

ExportDetails.defaultProps = {
  enableExportDetails: false,
  resourceType: '',
  classNames: '',
};

function mapStateToProps(state) {
  return {
    featureFlagExportAllAssignments: state.featureFlag.export_all_assignments,
    teacherSubjects: state.userData.teacherSubjects,
  };
}

export default connect(mapStateToProps)(ExportDetails);
